// lazy loaded app
import { useEffect, lazy, Suspense } from 'react'
import { useDispatch } from 'react-redux'
import { Outlet } from 'react-router-dom'
import { useAuth } from "react-oidc-context"
import { setAllWallets, setShowAddWallet } from './redux/userReducer'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import useNotifications from './components/Hooks/useNotifications'
import Spinner from 'components/Spinner'
import { useNavigate } from 'react-router-dom';

const App = () => {
 const dispatch = useDispatch()
 const auth = useAuth()
 const navigate = useNavigate()

 const getWallets = async () => {
   let wallets = sessionStorage.getItem('wallets')

   if (wallets) {
     wallets = JSON.parse(wallets)
     if (wallets && wallets.length > 0) {
       dispatch(setAllWallets(wallets))
       dispatch(setShowAddWallet(false))
     } else {
       dispatch(setShowAddWallet(true))
     }
   } else {
     dispatch(setShowAddWallet(true))
   }
 }

 // Check authentication status and redirect if needed
 useEffect(() => {
   // Keep existing auth redirect check
   if (!auth.isAuthenticated && !auth.isLoading && !auth.activeNavigator && !auth.error) {
     auth.signinRedirect();
   }
   
   // Add trial check (only runs when authenticated)
   if (auth.isAuthenticated && auth.user) {
     try {
       const trialEndDate = new Date(auth.user.profile['custom:trial_end_date']);
       const currentDate = new Date();
       
       if (currentDate > trialEndDate) {
         navigate('/billing');
       }
     } catch (error) {
       console.error("Error checking trial status");
       // Handle gracefully for users without trial attributes
     }
   }
 }, [auth.isAuthenticated, auth.isLoading, auth.activeNavigator, auth.error, auth.user]);

 // Add auto-refresh
 useEffect(() => {
   const interval = setInterval(() => {
     if (auth.user?.expires_at) {
       const expiresIn = auth.user.expires_at - Date.now() / 1000;
       if (expiresIn < 300) { // Refresh 5 minutes before expiry
        auth.signinSilent();
       }
     }
   }, 60000); // Check every minute
   
   return () => clearInterval(interval);
 }, [auth.user]);

 // Get wallets when authenticated
 useEffect(() => {
   if (auth.isAuthenticated) {
     getWallets()
   }
 }, [auth.isAuthenticated])

 useNotifications()

 if (auth.isLoading) {
   return (
     <div className="flex justify-center items-center h-screen">
       <Spinner />
     </div>
   );
 }

 // stylized authentication error page
 if (auth.error) {
   return (
     <div className="flex justify-center items-center">
       <div className="bg-[rgba(23,23,30,0.95)] border border-[rgba(115,246,255,0.1)] rounded-lg p-2 w-64 shadow-md">
         <div className="flex items-center text-[#73F6FF] mb-2">
           <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
           </svg>
           <h2 className="text-xs font-medium">Auth Error</h2>
         </div>
         {/* <p className="text-[rgba(115,246,255,0.8)] text-[10px] mb-2 truncate">{auth.error.message}</p> */}
         <p className="text-[rgba(115,246,255,0.8)] text-[10px] mb-2 truncate">Authentication failed. Please try again.</p>
         <button 
           onClick={() => window.location.reload()} 
           className="w-full bg-[rgba(23,23,30,0.4)] border border-[rgba(115,246,255,0.08)] text-[rgba(115,246,255,0.7)] text-[10px] uppercase tracking-wider py-1 px-2 rounded transition duration-200 hover:bg-[rgba(23,23,30,0.6)] hover:border-[rgba(115,246,255,0.2)] hover:text-[#73F6FF] hover:shadow-[0_0_15px_rgba(115,246,255,0.1)]"
         >
           Retry
         </button>
       </div>
     </div>
   );
 }

 // Only render the app content when authenticated
 if (auth.isAuthenticated) {
   return (
     <div>
       <div className="blob"></div>
       <ToastContainer
         position="top-right"
         autoClose={15000}
         icon={false}
         limit={3}
         newestOnTop
         closeOnClick={false}
         rtl={false}
         pauseOnFocusLoss
         draggable={false}
         theme="dark"
         style={{
           maxWidth: '100%',
           width: 'auto'
         }}
       />
       <Suspense fallback={<Spinner />}>
         <Outlet />
       </Suspense>
     </div>
   )
 }

 // Return null while waiting for redirect
 return null
}

export default App